import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi } from "../../api/endpoints";
import type { CardType, Stage, Template } from "../../types/domain";
import { IcoCheck, IcoClose, IcoTemplate, IcoTrash } from "../../components/icons";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { isTouchLike } from "../../lib/device";
import { useCanWrite } from "../../hooks/useCanWrite";

const STAGE_META: Record<Stage, { label: string; color: string }> = {
  warmup: { label: "Прогрев", color: "#3B82F6" },
  sales: { label: "Продажи", color: "#14B8A6" },
  close: { label: "Дожим", color: "#F59E0B" },
};

interface Props {
  template: Template | null; // null → create
  types: CardType[];
  onClose: () => void;
}

interface Draft {
  name: string;
  typeId: string;
  stage: Stage;
  subject: string;
  preview: string;
  time: string;
  tags: string[];
}

const emptyDraft = (types: CardType[]): Draft => ({
  name: "",
  typeId:
    types.find((t) => t.id === "t_email")?.id ?? types[0]?.id ?? "",
  stage: "warmup",
  subject: "",
  preview: "",
  time: "",
  tags: [],
});

export function TemplateEditor({ template, types, onClose }: Props) {
  const qc = useQueryClient();
  const canWrite = useCanWrite();
  const readOnly = !canWrite;
  const isNew = template === null;

  const [draft, setDraft] = useState<Draft>(() =>
    template
      ? {
          name: template.name,
          typeId: template.typeId,
          stage: template.stage,
          subject: template.subject ?? "",
          preview: template.preview ?? "",
          time: template.time ?? "",
          tags: template.tags ?? [],
        }
      : emptyDraft(types),
  );
  const [tagInput, setTagInput] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const createMutation = useMutation({
    mutationFn: () =>
      templatesApi.create({
        name: draft.name,
        typeId: draft.typeId,
        stage: draft.stage,
        subject: draft.subject,
        preview: draft.preview,
        time: draft.time || null,
        tags: draft.tags,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const patchMutation = useMutation({
    mutationFn: () =>
      templatesApi.patch(template!.id, {
        name: draft.name,
        typeId: draft.typeId,
        stage: draft.stage,
        subject: draft.subject,
        preview: draft.preview,
        time: draft.time || null,
        tags: draft.tags,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => templatesApi.remove(template!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const addTagFromInput = () => {
    const v = tagInput.trim();
    if (!v) return;
    if (!draft.tags.includes(v)) {
      setDraft({ ...draft, tags: [...draft.tags, v] });
    }
    setTagInput("");
  };

  const removeTag = (t: string) => {
    setDraft({ ...draft, tags: draft.tags.filter((x) => x !== t) });
  };

  const canSave = draft.name.trim().length > 0 && draft.typeId;
  const saving = createMutation.isPending || patchMutation.isPending;
  const handleSave = () => {
    if (!canSave) return;
    if (isNew) createMutation.mutate();
    else patchMutation.mutate();
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="launch-dot" style={{ background: "var(--accent-soft)" }}>
            <IcoTemplate size={13} stroke="var(--accent-text)" />
          </div>
          <h2>{isNew ? "Новый шаблон" : readOnly ? "Шаблон" : "Редактирование шаблона"}</h2>
          <div className="spacer" />
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">
            <IcoClose size={16} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <label className="form-label">Название шаблона</label>
            <input
              className="form-input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Например: Утреннее напоминание об эфире"
              autoFocus={!readOnly && !isTouchLike()}
              disabled={readOnly}
            />
          </div>

          <div className="form-row split">
            <div>
              <label className="form-label">Тип карточки</label>
              <select
                className="form-select"
                value={draft.typeId}
                onChange={(e) => setDraft({ ...draft, typeId: e.target.value })}
                disabled={readOnly}
              >
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Стадия запуска</label>
              <div className="seg seg-inline" style={{ width: "100%" }}>
                {(Object.entries(STAGE_META) as Array<[Stage, (typeof STAGE_META)[Stage]]>).map(([k, m]) => (
                  <button
                    type="button"
                    key={k}
                    className={draft.stage === k ? "active" : ""}
                    onClick={() => !readOnly && setDraft({ ...draft, stage: k })}
                    disabled={readOnly}
                    style={{ flex: 1 }}
                  >
                    <span className="stage-dot" style={{ background: m.color }} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="form-row split">
            <div>
              <label className="form-label">Тема / заголовок</label>
              <input
                className="form-input"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                placeholder="Используйте {{переменные}} для подстановки"
                disabled={readOnly}
              />
            </div>
            <div>
              <label className="form-label">Время по умолчанию</label>
              <input
                className="form-input"
                type="time"
                value={draft.time}
                onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Текст письма / превью</label>
            <textarea
              className="form-textarea form-textarea-lg"
              value={draft.preview}
              onChange={(e) => setDraft({ ...draft, preview: e.target.value })}
              placeholder="Полный текст. Переменные в двойных фигурных скобках: {{имя}}, {{дата}}."
              disabled={readOnly}
            />
          </div>

          <div className="form-row">
            <label className="form-label">Теги</label>
            <div className="templates-tag-editor">
              {draft.tags.map((t) => (
                <span key={t} className="templates-tag templates-tag-edit">
                  #{t}
                  {!readOnly && (
                    <button type="button" onClick={() => removeTag(t)} aria-label="Удалить тег">
                      <IcoClose size={10} />
                    </button>
                  )}
                </span>
              ))}
              {!readOnly && (
                <input
                  className="templates-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTagFromInput();
                    }
                  }}
                  onBlur={addTagFromInput}
                  placeholder={draft.tags.length === 0 ? "Добавить тег и Enter" : "+"}
                />
              )}
              {readOnly && draft.tags.length === 0 && (
                <span style={{ color: "var(--text-muted)", fontSize: 12 }}>нет тегов</span>
              )}
            </div>
          </div>
        </div>
        <div className="modal-foot">
          {!isNew && !readOnly && template && template.source !== "preset" && (
            <button
              type="button"
              className="btn-danger"
              onClick={() => {
                if (window.confirm(`Удалить шаблон «${template.name}»?`)) {
                  removeMutation.mutate();
                }
              }}
              disabled={removeMutation.isPending}
            >
              <IcoTrash size={13} /> Удалить
            </button>
          )}
          <div className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>
            {readOnly ? "Закрыть" : "Отмена"}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={!canSave || saving}
            >
              <IcoCheck size={14} /> {isNew ? "Создать шаблон" : "Сохранить"}
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
